import React, { createContext, useState, useEffect, useRef } from "react";

export const CreatorModeContext = createContext();

const CREATOR_MODE_STORAGE_KEY = "creatorModeEnabled";
const CREATOR_STUDIO_PATH = "/dashboard-creator-studio";
const DASHBOARD_PATH = "/dashboard";

// List of all creator tool routes - when user enters any of these, Creator Mode should auto-enable
const CREATOR_TOOL_ROUTES = [
  "/Image-edit",
  "/video-edit",
  "/video-recorder",
  "/voice-recorder",
  "/art-design",
  "/qr-scanner",
];

export default function CreatorModeProvider(props) {
  const navigationRef = useRef(null);
  const previousPathRef = useRef(null);
  const currentPathRef = useRef(window.location.pathname);

  // Load initial state from localStorage
  const [isCreatorMode, setIsCreatorMode] = useState(() => {
    const stored = localStorage.getItem(CREATOR_MODE_STORAGE_KEY);
    return stored === "true";
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(CREATOR_MODE_STORAGE_KEY, isCreatorMode.toString());
  }, [isCreatorMode]);

  // Handle navigation when Creator Mode is enabled or disabled
  useEffect(() => {
    if (!navigationRef.current) return;

    if (isCreatorMode) {
      // When Creator Mode is enabled, store current path as previous path
      const currentPath = window.location.pathname;
      const isCreatorToolRoute = CREATOR_TOOL_ROUTES.some((route) =>
        currentPath.startsWith(route)
      );
      
      // Store the previous path before navigating (only if not already a creator tool route)
      // If user manually enabled Creator Mode from a non-creator route, store that path
      if (!isCreatorToolRoute && currentPath !== CREATOR_STUDIO_PATH) {
        // Only store if we don't already have a previous path stored
        // (to preserve the original page user came from)
        if (!previousPathRef.current) {
          previousPathRef.current = currentPath;
        }
        
        // Navigate to creator studio
        setTimeout(() => {
          if (navigationRef.current) {
            navigationRef.current(CREATOR_STUDIO_PATH);
          }
        }, 100);
      } else if (isCreatorToolRoute) {
        // If user is on a creator tool route, store the path they came from
        // (This will be set when they first entered the creator tool)
        // Don't navigate away - they're already where they want to be
      }
    } else {
      // When Creator Mode is disabled, navigate back to previous page
      const currentPath = window.location.pathname;
      const isCreatorToolRoute = CREATOR_TOOL_ROUTES.some((route) =>
        currentPath.startsWith(route)
      );
      
      // Determine where to navigate
      let targetPath = previousPathRef.current || DASHBOARD_PATH;
      
      // If we're on a creator tool or creator studio, navigate to previous page
      if (isCreatorToolRoute || currentPath === CREATOR_STUDIO_PATH) {
        // Use previous path if available, otherwise fallback to dashboard
        if (targetPath && targetPath !== currentPath) {
          setTimeout(() => {
            if (navigationRef.current) {
              navigationRef.current(targetPath);
              // Clear the previous path after navigation
              previousPathRef.current = null;
            }
          }, 100);
        } else {
          // Fallback to dashboard if no previous path
          setTimeout(() => {
            if (navigationRef.current) {
              navigationRef.current(DASHBOARD_PATH);
            }
          }, 100);
        }
      }
    }
  }, [isCreatorMode]);

  // Handle initial load - if Creator Mode is enabled, navigate to creator studio
  // BUT only if user is not already on a creator tool route
  useEffect(() => {
    if (isCreatorMode) {
      const currentPath = window.location.pathname;
      const isCreatorToolRoute = CREATOR_TOOL_ROUTES.some((route) =>
        currentPath.startsWith(route)
      );
      
      // Only navigate to creator studio if:
      // 1. Not already on creator studio page
      // 2. Not on a creator tool route (user should stay on the tool they're using)
      if (currentPath !== CREATOR_STUDIO_PATH && !isCreatorToolRoute && navigationRef.current) {
        // Wait a bit for navigation handler to be set
        const timer = setTimeout(() => {
          if (navigationRef.current) {
            navigationRef.current(CREATOR_STUDIO_PATH);
          }
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, []); // Run only on mount

  // Auto-enable Creator Mode when entering any creator tool route
  useEffect(() => {
    const checkAndEnableCreatorMode = () => {
      const currentPath = window.location.pathname;
      
      // Only check if pathname actually changed
      if (currentPath === currentPathRef.current) {
        return;
      }
      
      const previousPath = currentPathRef.current;
      currentPathRef.current = currentPath;
      const isCreatorToolRoute = CREATOR_TOOL_ROUTES.some((route) =>
        currentPath.startsWith(route)
      );

      // If user is on a creator tool route and Creator Mode is not enabled, enable it
      if (isCreatorToolRoute && !isCreatorMode) {
        // Store the previous path before enabling Creator Mode
        // This is the page they came from before entering the creator tool
        const isPreviousPathCreatorTool = previousPath && CREATOR_TOOL_ROUTES.some((route) =>
          previousPath.startsWith(route)
        );
        
        if (previousPath && !isPreviousPathCreatorTool && previousPath !== CREATOR_STUDIO_PATH) {
          previousPathRef.current = previousPath;
        } else if (!previousPathRef.current) {
          // If no previous path stored and we can't determine it, use dashboard as fallback
          previousPathRef.current = DASHBOARD_PATH;
        }
        setIsCreatorMode(true);
      }
    };

    // Check immediately on mount
    checkAndEnableCreatorMode();

    // Listen for route changes via popstate (browser back/forward)
    const handlePopState = () => {
      checkAndEnableCreatorMode();
    };
    window.addEventListener("popstate", handlePopState);

    // Listen for route changes via pushState/replaceState (React Router navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      setTimeout(checkAndEnableCreatorMode, 0);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      setTimeout(checkAndEnableCreatorMode, 0);
    };

    // Also check periodically as a fallback (React Router might not always trigger the above)
    const interval = setInterval(checkAndEnableCreatorMode, 300);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      clearInterval(interval);
    };
  }, [isCreatorMode]);

  // Handle browser back button when Creator Mode is active
  // Allow back navigation to previous page (which will disable Creator Mode)
  useEffect(() => {
    if (isCreatorMode) {
      // Push current state to history stack to create a barrier
      // This creates an entry that we can detect when user tries to go back
      window.history.pushState({ creatorMode: true, preventBack: true }, "", window.location.href);

      let isHandlingPopState = false;

      const handlePopState = (event) => {
        // If trying to go back while in Creator Mode
        if (isCreatorMode && !isHandlingPopState) {
          isHandlingPopState = true;
          
          // Get the path the browser tried to navigate to
          const attemptedPath = window.location.pathname;
          const currentPathBeforePop = currentPathRef.current;
          
          // Update current path ref
          currentPathRef.current = attemptedPath;
          
          const isCreatorToolRoute = CREATOR_TOOL_ROUTES.some((route) =>
            attemptedPath.startsWith(route)
          );
          
          // If user is on a creator tool or creator studio and tries to go back,
          // allow it by disabling Creator Mode and navigating to previous page
          if ((isCreatorToolRoute || attemptedPath === CREATOR_STUDIO_PATH) && navigationRef.current) {
            // Prevent the browser's back navigation by pushing state back
            const currentUrl = window.location.href;
            window.history.pushState({ creatorMode: true, preventBack: true }, "", currentUrl);
            
            // Disable Creator Mode - this will trigger navigation to previous page
            setIsCreatorMode(false);
            
            // Reset flag after a short delay
            setTimeout(() => {
              isHandlingPopState = false;
            }, 100);
          } else {
            // For other pages, prevent back navigation
            const currentUrl = window.location.href;
            window.history.pushState({ creatorMode: true, preventBack: true }, "", currentUrl);
            
            console.warn("Back navigation is disabled in Creator Mode. Please disable Creator Mode first.");
            
            setTimeout(() => {
              isHandlingPopState = false;
            }, 100);
          }
        }
      };

      // Add popstate listener - this fires when user tries to navigate back
      window.addEventListener("popstate", handlePopState);

      // Cleanup
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [isCreatorMode]);

  const toggleCreatorMode = () => {
    setIsCreatorMode((prev) => !prev);
  };

  const enableCreatorMode = () => {
    setIsCreatorMode(true);
  };

  const disableCreatorMode = () => {
    setIsCreatorMode(false);
  };

  const setNavigationHandler = (handler) => {
    navigationRef.current = handler;
  };

  const value = {
    isCreatorMode,
    toggleCreatorMode,
    enableCreatorMode,
    disableCreatorMode,
    setNavigationHandler,
    previousPath: previousPathRef.current,
  };

  return (
    <CreatorModeContext.Provider value={value}>
      {props.children}
    </CreatorModeContext.Provider>
  );
}

